/**
 * Referrals to external clinicians: the practice-side API (`controllers/referralController.js`, mounted at
 * `/api/referrals`). Every call names the selected practice; the server checks membership and role itself.
 */

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  ExternalNoteForReview,
  NewReferralForm,
  PatientReferral,
  ReferralAcknowledgement,
  SummaryDraft,
  referralRequestBody,
  ReferralNetwork,
  ReferralNetworkLink,
  ReferralRecipientOption,
  ShareableFile,
  UnsharedEntry,
  EntryShareCount,
  SharedEntryOutcome,
  IncomingPracticeReferral,
  ShareableSourceType,
  PracticeEntryForReview,
  OnwardNotice,
  DemographicProposal,
  DemographicDecision,
  NetworkContactLink,
  ReferralChatContext,
} from '../models/referral.model';
import { environment } from '../../../environments/environment';
import { GroupService } from './group.service';
import { envFlag } from '../../shared/util/env-flag.util';
import { isStateModeGroup } from '../../shared/util/state-mode.util';

/** Fail-closed feature flag: an environment file without `referralsEnabled` hides referrals. */
export const REFERRALS_ENABLED = envFlag(environment, 'referralsEnabled');

/** Referrals for one practice: the build flag, and never for a state unit (tasks/state-mode.md). */
export function referralsEnabledFor(group: { stateMode?: boolean } | null | undefined): boolean {
  return REFERRALS_ENABLED && !isStateModeGroup(group);
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class ReferralService {
  private apiUrl = 'api/referrals';

  constructor(private http: HttpClient, private groupService: GroupService) {}

  private get groupId(): string {
    return this.groupService.group?.id;
  }

  /* ── network (admins) ─────────────────────────────────────────────────────────── */

  listNetwork(): Observable<ReferralNetwork> {
    return this.http.get<Envelope<ReferralNetworkLink[]> & { practiceLinksEnabled?: boolean; canRespond?: boolean }>(`${this.apiUrl}/group/${this.groupId}/network`, this.authOptions())
      .pipe(map((r) => ({ links: r.data || [], practiceLinksEnabled: r.practiceLinksEnabled === true, canRespond: r.canRespond === true })));
  }

  /**
   * Who a practice member may chat 1-on-1 with across the referral network (2026-09-22): per active link, the linked
   * practice's doctors or the external clinician. Any member may ask (the server checks membership, not role).
   */
  networkContacts(groupId: string = this.groupId): Observable<NetworkContactLink[]> {
    return this.http.get<Envelope<NetworkContactLink[]>>(`${this.apiUrl}/group/${groupId}/network-contacts`, this.authOptions())
      .pipe(map((r) => r.data || []));
  }

  /** What a referral chat shows beside its messages: the summary sent and the official responses (phase 5). */
  chatContext(groupId: string, patientId: string, linkId: string): Observable<ReferralChatContext> {
    const params = new HttpParams().set('patientId', patientId).set('linkId', linkId);
    return this.http.get<Envelope<ReferralChatContext>>(`${this.apiUrl}/group/${groupId}/chat-context`, { ...this.authOptions(), params })
      .pipe(map((r) => r.data || { linkKind: 'practice', items: [] }));
  }

  /* ── practice links (§19) ──────────────────────────────────────────────────────── */

  getPracticeCode(): Observable<{ code: string; createdAt: string }> {
    return this.http.get<Envelope<{ code: string; createdAt: string }>>(`${this.apiUrl}/group/${this.groupId}/network/code`, this.authOptions()).pipe(map((r) => r.data));
  }

  rotatePracticeCode(): Observable<{ code: string; createdAt: string }> {
    return this.http.post<Envelope<{ code: string; createdAt: string }>>(`${this.apiUrl}/group/${this.groupId}/network/code/rotate`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  requestPracticeLink(code: string): Observable<{ linkId: string; status: string; alreadyInNetwork: boolean; acceptedTheirRequest?: boolean; awaitingOwner?: boolean }> {
    return this.http.post<Envelope<{ linkId: string; status: string; alreadyInNetwork: boolean; acceptedTheirRequest?: boolean; awaitingOwner?: boolean }>>(
      `${this.apiUrl}/group/${this.groupId}/network/practices`, { code }, this.authOptions()).pipe(map((r) => r.data));
  }

  /** Only the practice owner may answer (owner, 2026-09-22); the server refuses anyone else with NOT_PRACTICE_OWNER. */
  respondPracticeLink(linkId: string, response: 'accept' | 'decline', groupId: string = this.groupId): Observable<{ responded: boolean; status: string }> {
    return this.http.post<Envelope<{ responded: boolean; status: string }>>(
      `${this.apiUrl}/group/${groupId}/network/${linkId}/respond`, { response }, this.authOptions()).pipe(map((r) => r.data));
  }

  /** Emails this practice's referral code to an address, so a practice already on MedicAI can link with it. */
  emailPracticeCode(email: string): Observable<{ sent: boolean }> {
    return this.http.post<Envelope<{ sent: boolean }>>(`${this.apiUrl}/group/${this.groupId}/network/code/email`, { email }, this.authOptions()).pipe(map((r) => r.data));
  }

  /**
   * The one invite path (owner, 2026-09-22; tasks/referral-invite-unified.md): the server decides whether this becomes a
   * link request to another MedicAI practice, the practice code by email, or an invite to MedicAI Referrals. It says only
   * that it was sent.
   */
  invite(body: { email: string; name?: string | null; hpcsaNumber?: string | null }): Observable<{ sent: boolean; alreadyInNetwork: boolean }> {
    return this.http.post<Envelope<{ sent: boolean; alreadyInNetwork: boolean }>>(
      `${this.apiUrl}/group/${this.groupId}/network/invite`, body, this.authOptions()).pipe(map((r) => r.data));
  }

  endLink(linkId: string): Observable<{ ended: boolean; status: string }> {
    return this.http.post<Envelope<{ ended: boolean; status: string }>>(
      `${this.apiUrl}/group/${this.groupId}/network/${linkId}/end`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  /* ── referrals ────────────────────────────────────────────────────────────────── */

  /** `switchedOff`: links this practice has that this server cannot send to, because that kind of referral is off. */
  listRecipients(): Observable<{ options: ReferralRecipientOption[]; acknowledgement: ReferralAcknowledgement; switchedOff: number }> {
    return this.http.get<Envelope<ReferralRecipientOption[]> & { acknowledgement: ReferralAcknowledgement; switchedOff?: number }>(
      `${this.apiUrl}/group/${this.groupId}/recipients`, this.authOptions())
      .pipe(map((r) => ({ options: r.data || [], acknowledgement: r.acknowledgement, switchedOff: r.switchedOff || 0 })));
  }

  listPatientReferrals(patientId: string, groupId: string = this.groupId): Observable<PatientReferral[]> {
    return this.http.get<Envelope<PatientReferral[]>>(`${this.apiUrl}/group/${groupId}/patients/${patientId}`, this.authOptions())
      .pipe(map((r) => r.data || []));
  }

  /** `replyToReceiptId`: this referral shares back on a referral another practice sent (§19, P8). */
  createReferral(patientId: string, form: NewReferralForm, acknowledgementVersion: string, replyToReceiptId: string | null = null):
  Observable<{ referralId: string; sentNow: number; awaitingLink: number; shortenedFields?: string[]; sentRecipientIds?: string[] }> {
    const body = { ...referralRequestBody(form, acknowledgementVersion), ...(replyToReceiptId ? { replyToReceiptId } : {}) };
    return this.http.post<Envelope<{ referralId: string; sentNow: number; awaitingLink: number; sentRecipientIds?: string[] }>>(
      `${this.apiUrl}/group/${this.groupId}/patients/${patientId}`, body, this.authOptions())
      .pipe(map((r) => r.data));
  }

  /** §21.7: widen a referral of chosen entries into the whole patient file. One way. */
  widenReferral(recipientId: string, futureMode: string, futureUntil: string | null, acknowledgementVersion: string):
  Observable<{ recipientId: string; shareMode: string; futureMode: string; futureUntil: string | null }> {
    const body = { futureMode, futureUntil, acknowledged: true, acknowledgementVersion };
    return this.http.post<Envelope<{ recipientId: string; shareMode: string; futureMode: string; futureUntil: string | null }>>(
      `${this.apiUrl}/group/${this.groupId}/recipients/${recipientId}/widen`, body, this.authOptions()).pipe(map((r) => r.data));
  }

  /* ── one entry of the patient file, shared with a link (§21) ─────────────────── */

  /** How many links each entry of this patient has reached. Empty when referrals are switched off. */
  listEntryShareCounts(patientId: string): Observable<EntryShareCount[]> {
    return this.http.get<Envelope<EntryShareCount[]>>(`${this.apiUrl}/group/${this.groupId}/patients/${patientId}/entry-shares`, this.authOptions())
      .pipe(map((r) => r.data || []));
  }

  /**
   * Shares ONE entry with the chosen links. A link with no referral of chosen entries for this patient gets one, so
   * this may create a referral - which is why it asks for the same acknowledgement a referral does.
   */
  shareEntryWithLinks(patientId: string, sourceType: ShareableSourceType, sourceId: string, linkIds: string[], acknowledgementVersion: string):
  Observable<{ sourceType: string; sourceId: string; shared: SharedEntryOutcome[] }> {
    const body = { sourceType, sourceId, linkIds, acknowledged: true, acknowledgementVersion };
    return this.http.post<Envelope<{ sourceType: string; sourceId: string; shared: SharedEntryOutcome[] }>>(
      `${this.apiUrl}/group/${this.groupId}/patients/${patientId}/entry-shares`, body, this.authOptions()).pipe(map((r) => r.data));
  }

  /* ── referrals received from another practice (§19) ────────────────────────────── */

  getIncomingReferral(receiptId: string, groupId: string = this.groupId): Observable<IncomingPracticeReferral> {
    return this.http.get<Envelope<IncomingPracticeReferral>>(`${this.apiUrl}/group/${groupId}/incoming/${receiptId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  acceptIncomingReferral(receiptId: string, body: Record<string, unknown>, groupId: string = this.groupId): Observable<{ patientId: string; createdPatient: boolean; replyReferralId: string | null; shareBack: string; summaryNoteId: string | null }> {
    return this.http.post<Envelope<{ patientId: string; createdPatient: boolean; replyReferralId: string | null; shareBack: string; summaryNoteId: string | null }>>(
      `${this.apiUrl}/group/${groupId}/incoming/${receiptId}/accept`, body, this.authOptions()).pipe(map((r) => r.data));
  }

  getOnwardNotice(noticeId: string, groupId: string = this.groupId): Observable<OnwardNotice> {
    return this.http.get<Envelope<OnwardNotice>>(`${this.apiUrl}/group/${groupId}/onward-notices/${noticeId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  acknowledgeOnwardNotice(noticeId: string, groupId: string = this.groupId): Observable<{ acknowledged: boolean }> {
    return this.http.post<Envelope<{ acknowledged: boolean }>>(`${this.apiUrl}/group/${groupId}/onward-notices/${noticeId}/acknowledge`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  getDemographicProposal(proposalId: string, groupId: string = this.groupId): Observable<DemographicProposal> {
    return this.http.get<Envelope<DemographicProposal>>(`${this.apiUrl}/group/${groupId}/demographic-proposals/${proposalId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  resolveDemographicProposal(proposalId: string, decisions: Record<string, DemographicDecision>, groupId: string = this.groupId): Observable<{ applied: string[] }> {
    return this.http.post<Envelope<{ applied: string[] }>>(`${this.apiUrl}/group/${groupId}/demographic-proposals/${proposalId}/resolve`, { decisions }, this.authOptions()).pipe(map((r) => r.data));
  }

  getPracticeEntry(entryId: string, groupId: string = this.groupId): Observable<PracticeEntryForReview> {
    return this.http.get<Envelope<PracticeEntryForReview>>(`${this.apiUrl}/group/${groupId}/practice-entries/${entryId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  validatePracticeEntry(entryId: string, groupId: string = this.groupId): Observable<{ patientNoteId: string; alreadyFiled: boolean }> {
    return this.http.post<Envelope<{ patientNoteId: string; alreadyFiled: boolean }>>(`${this.apiUrl}/group/${groupId}/practice-entries/${entryId}/validate`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  dismissPracticeEntry(entryId: string, reason: string, groupId: string = this.groupId): Observable<{ alreadyDismissed: boolean }> {
    return this.http.post<Envelope<{ alreadyDismissed: boolean }>>(`${this.apiUrl}/group/${groupId}/practice-entries/${entryId}/dismiss`, { reason }, this.authOptions()).pipe(map((r) => r.data));
  }

  declineIncomingReferral(receiptId: string, reason: string, groupId: string = this.groupId): Observable<{ declined: boolean }> {
    return this.http.post<Envelope<{ declined: boolean }>>(`${this.apiUrl}/group/${groupId}/incoming/${receiptId}/decline`, { reason }, this.authOptions()).pipe(map((r) => r.data));
  }

  /** An AI draft of the patient-file summary for a summary referral (§15). Uses the practice's AI tokens; not stored. */
  /**
   * An AI draft of the summary. With `streamId` it is also streamed on 'aiOutputUpdate-referralSummary' as it is written;
   * `instruction` with `currentText` refines the current draft. The answer is always the whole draft.
   */
  createSummaryDraft(patientId: string, body: { reason: string; linkIds: string[]; streamId?: string; instruction?: string; currentText?: string }): Observable<SummaryDraft> {
    return this.http.post<Envelope<SummaryDraft>>(
      `${this.apiUrl}/group/${this.groupId}/patients/${patientId}/summary-draft`, body, this.authOptions()).pipe(map((r) => r.data));
  }

  /** §16 M2: the files a summary may send, as picker tiles (references and 10-minute preview links). */
  listShareableFiles(patientId: string): Observable<{ files: ShareableFile[]; maxFiles: number }> {
    return this.http.get<Envelope<{ files: ShareableFile[]; maxFiles: number }>>(
      `${this.apiUrl}/group/${this.groupId}/patients/${patientId}/shareable-files`, this.authOptions()).pipe(map((r) => r.data));
  }

  /** §18 V6: the entries this recipient does not have, and why. */
  listUnsharedEntries(recipientId: string): Observable<UnsharedEntry[]> {
    return this.http.get<Envelope<UnsharedEntry[]>>(
      `${this.apiUrl}/group/${this.groupId}/recipients/${recipientId}/unshared-entries`, this.authOptions()).pipe(map((r) => r.data));
  }

  stopSharing(recipientId: string): Observable<{ stopped: boolean; status: string }> {
    return this.http.post<Envelope<{ stopped: boolean; status: string }>>(
      `${this.apiUrl}/group/${this.groupId}/recipients/${recipientId}/stop`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  /* ── incoming external notes ──────────────────────────────────────────────────── */

  getExternalNote(entryId: string, groupId: string = this.groupId): Observable<ExternalNoteForReview> {
    return this.http.get<Envelope<ExternalNoteForReview>>(`${this.apiUrl}/group/${groupId}/inbound/${entryId}`, this.authOptions())
      .pipe(map((r) => r.data));
  }

  validateExternalNote(entryId: string, groupId: string = this.groupId): Observable<{ patientNoteId: string; alreadyFiled: boolean }> {
    return this.http.post<Envelope<{ patientNoteId: string; alreadyFiled: boolean }>>(
      `${this.apiUrl}/group/${groupId}/inbound/${entryId}/validate`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  dismissExternalNote(entryId: string, reason: string, groupId: string = this.groupId): Observable<{ alreadyDismissed: boolean }> {
    return this.http.post<Envelope<{ alreadyDismissed: boolean }>>(
      `${this.apiUrl}/group/${groupId}/inbound/${entryId}/dismiss`, { reason }, this.authOptions()).pipe(map((r) => r.data));
  }

  /** §16 R4: ask the referrals platform again for an external note's files that ended without arriving. */
  requestExternalNoteFilesAgain(entryId: string, groupId: string = this.groupId): Observable<{ requested: number }> {
    return this.http.post<Envelope<{ requested: number }>>(
      `${this.apiUrl}/group/${groupId}/inbound/${entryId}/files/request-again`, {}, this.authOptions()).pipe(map((r) => r.data));
  }

  private authOptions() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }
}
